import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Images } from "lucide-react";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/dashboard/ui";
import { PhotoMasonry } from "@/components/gallery/PhotoMasonry";
import { Lightbox } from "@/components/gallery/Lightbox";
import { ClientNav, SubmitBar } from "@/components/client/ClientChrome";
import { SubmitConfirm, SubmitSuccess } from "@/components/client/SubmitFlow";
import { LeadModal } from "@/components/client/LeadModal";
import { useClientGallery } from "@/lib/useClientGallery";

export default function ClientGallery() {
  const { eventId } = useParams();
  const g = useClientGallery(eventId);
  const [idx, setIdx] = useState(null);
  const [cat, setCat] = useState("All");
  const [confirm, setConfirm] = useState(false);
  const [success, setSuccess] = useState(0);
  const [leadPhoto, setLeadPhoto] = useState(null);

  const cats = useMemo(() => ["All", ...Array.from(new Set(g.photos.map((p) => p.category)))], [g.photos]);
  const list = cat === "All" ? g.photos : g.photos.filter((p) => p.category === cat);

  if (g.loading) return <div className="min-h-screen bg-cream"><Loader full label="Loading gallery" /></div>;
  if (g.error || !g.event) return <div className="min-h-screen flex items-center justify-center text-ink/60 bg-cream">Gallery not found.</div>;

  const doSubmit = async (comments) => {
    try { const r = await g.submit(comments); setConfirm(false); setSuccess(r.count); }
    catch { toast.error("Could not submit. Please try again."); }
  };

  return (
    <div className="min-h-screen bg-cream pb-28">
      <ClientNav eventId={eventId} eventName={g.event.name} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-serif text-4xl text-ink font-light">All Photos</h1>
            <p className="text-ink/60 text-sm mt-1">{g.photos.length} photographs · tap to select for your album</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap mb-6">
          {cats.map((c) => (
            <button key={c} data-testid={`cat-${c}`} onClick={() => setCat(c)} className={`text-sm rounded-full px-4 py-1.5 transition-colors ${cat === c ? "bg-maroon text-cream" : "bg-white border border-maroon/10 text-ink hover:border-gold"}`}>{c}</button>
          ))}
        </div>

        {list.length === 0 ? <EmptyState icon={Images} title="No photos yet" desc="Your photographer hasn't added photos to this category." />
          : <PhotoMasonry photos={list} onOpen={setIdx} selectable favoritable watermark={g.event.watermark}
              onToggleSelect={g.toggleSelect} onToggleFavorite={g.toggleFavorite} />}
      </div>

      <Lightbox photos={list} index={idx} onClose={() => setIdx(null)} setIndex={setIdx}
        selectable favoritable downloadable={g.event.download_permission} watermark={g.event.watermark}
        onToggleSelect={g.toggleSelect} onToggleFavorite={g.toggleFavorite} onDownload={setLeadPhoto} />

      <LeadModal photo={leadPhoto} eventId={eventId} onClose={() => setLeadPhoto(null)} />

      <SubmitBar count={g.selectedCount} onSubmit={() => setConfirm(true)} />
      <SubmitConfirm open={confirm} count={g.selectedCount} submitting={g.submitting} onCancel={() => setConfirm(false)} onConfirm={doSubmit} />
      <SubmitSuccess open={success > 0} count={success} onClose={() => setSuccess(0)} />
    </div>
  );
}
